import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, Volume2, BookOpen, Heart, Utensils, PartyPopper, Globe, Handshake, Gamepad2, Building, Megaphone, Film, X, ArrowLeft, Maximize, Camera } from 'lucide-react';
import Hls from 'hls.js';
import { getVideos, getCategoryCovers, getImages } from '../services/api';
import { useLanguage } from '../context/LanguageContext';
import { t } from '../translations';

const CF_CUSTOMER = 'customer-vbbn7p39z8nnfjwk.cloudflarestream.com';

// HLS Player that forces highest quality and detects orientation
const HlsPlayer = ({ videoId, autoplay = true, onOrientationDetected }) => {
  const videoRef = useRef(null);
  const hlsRef = useRef(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !videoId) return;

    const manifestUrl = `https://${CF_CUSTOMER}/${videoId}/manifest/video.m3u8`;

    if (Hls.isSupported()) {
      const hls = new Hls({ startLevel: -1, capLevelToPlayerSize: false });
      hlsRef.current = hls;
      hls.loadSource(manifestUrl);
      hls.attachMedia(video);
      hls.on(Hls.Events.MANIFEST_PARSED, (_, data) => {
        // Force highest quality level
        const maxLevel = data.levels.length - 1;
        hls.currentLevel = maxLevel;
        hls.loadLevel = maxLevel;
        // Detect orientation from the highest quality level
        const topLevel = data.levels[maxLevel];
        if (topLevel && onOrientationDetected) {
          onOrientationDetected(topLevel.height > topLevel.width);
        }
        if (autoplay) video.play().catch(() => {});
      });
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      // Safari native HLS
      video.src = manifestUrl;
      video.addEventListener('loadedmetadata', () => {
        if (onOrientationDetected) {
          onOrientationDetected(video.videoHeight > video.videoWidth);
        }
      }, { once: true });
      if (autoplay) video.play().catch(() => {});
    }

    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [videoId, autoplay, onOrientationDetected]);

  return (
    <video
      ref={videoRef}
      className="w-full h-full object-contain bg-black"
      controls
      playsInline
      data-testid="hls-video-player"
    />
  );
};

// Generate thumbnail from cloudflare_uid when thumbnail_url is missing (production fix)
const getThumbUrl = (video) => {
  if (video.thumbnail_url) return video.thumbnail_url;
  if (video.cloudflare_uid) return `https://videodelivery.net/${video.cloudflare_uid}/thumbnails/thumbnail.jpg?height=1080`;
  return null;
};

const CATEGORIES = [
  { key: 'bible_tutoring', label: { en: 'Bible Teachings, Tutoring & English Class', es: 'Enseñanzas Bíblicas, Tutoría y Clase de Inglés' }, icon: BookOpen, color: 'from-blue-600/80 to-blue-800/80' },
  { key: 'children_stories_vlogs', label: { en: 'Children Stories & Vlog Reels', es: 'Historias de los Niños y Vlogs' }, icon: Film, color: 'from-purple-600/80 to-purple-800/80' },
  { key: 'gods_love_action', label: { en: "Showing God's Love in Action", es: 'Mostrando el Amor de Dios en Acción' }, icon: Heart, color: 'from-rose-600/80 to-rose-800/80' },
  { key: 'serving_food', label: { en: 'Serving Food', es: 'Sirviendo Comida' }, icon: Utensils, color: 'from-amber-600/80 to-amber-800/80' },
  { key: 'joyful_moments_bdays', label: { en: 'Joyful Moments & Birthdays', es: 'Momentos de Alegría y Cumpleaños' }, icon: PartyPopper, color: 'from-yellow-500/80 to-orange-700/80' },
  { key: 'local_outreach_events', label: { en: 'Local Outreach & Events', es: 'Alcance Local y Eventos' }, icon: Globe, color: 'from-teal-600/80 to-teal-800/80' },
  { key: 'volunteers_partners_action', label: { en: 'Volunteers & Partners in Action', es: 'Voluntarios y Socios en Acción' }, icon: Handshake, color: 'from-emerald-600/80 to-emerald-800/80' },
  { key: 'fun_activities', label: { en: 'Fun Activities', es: 'Actividades Divertidas' }, icon: Gamepad2, color: 'from-pink-500/80 to-pink-700/80' },
  { key: 'needs_construction', label: { en: 'Needs, Future Projects, Construction', es: 'Necesidades, Proyectos Futuros, Construcción' }, icon: Building, color: 'from-slate-500/80 to-slate-700/80' },
  { key: 'thank_you_shoutouts', label: { en: 'Thank You Shoutouts', es: 'Agradecimientos' }, icon: Megaphone, color: 'from-red-500/80 to-red-700/80' },
];

const LEGACY_MAP = {
  'bible_teachings': 'bible_tutoring',
  'childrens_stories': 'children_stories_vlogs',
  'gods_love': 'gods_love_action',
  'joyful_moments': 'joyful_moments_bdays',
  'local_outreach': 'local_outreach_events',
  'volunteers_partners': 'volunteers_partners_action',
  'construction': 'needs_construction',
  'future_projects': 'needs_construction',
};

// Video Card — simple thumbnail + duration, NO lazy loading (fixes iOS Safari)
const VideoCard = ({ video, isActive, onClick, language }) => {
  const thumb = getThumbUrl(video);
  return (
    <div
      className={`group relative rounded-xl overflow-hidden bg-slate-900 border transition-all duration-300 aspect-video cursor-pointer ${
        isActive ? 'border-amber-400 ring-2 ring-amber-400/40' : 'border-white/5 hover:border-white/20'
      }`}
      onClick={onClick}
      data-testid={`video-card-${video.id}`}
    >
      {thumb && (
        <img
          src={thumb}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-60 group-hover:opacity-40 transition-opacity" />
      {!isActive && (
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <div className="w-14 h-14 bg-amber-400 rounded-full flex items-center justify-center shadow-[0_0_25px_rgba(252,211,77,0.4)]">
            <Play className="w-6 h-6 text-slate-950 fill-slate-950 ml-0.5" />
          </div>
        </div>
      )}
      {video.duration && video.duration !== '0:00' && (
        <span className="absolute bottom-2 right-2 text-[10px] text-white/70 bg-black/50 px-1.5 py-0.5 rounded">{video.duration}</span>
      )}
      {isActive && (
        <div className="absolute top-2 left-2 flex items-center gap-1 bg-amber-400 text-slate-950 text-[10px] font-bold px-2 py-1 rounded-full">
          <Volume2 className="w-3 h-3" />
          {t('media.playing', language)}
        </div>
      )}
    </div>
  );
};

// Category Card
const CategoryCard = ({ category, videoCount, coverThumb, onClick, index, language }) => {
  const Icon = category.icon;
  return (
    <motion.div
      initial={{ opacity: 0, y: 25 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.06 }}
      whileHover={{ y: -4 }}
      className="group relative rounded-2xl overflow-hidden cursor-pointer aspect-[4/3] md:aspect-[3/2]"
      onClick={onClick}
      data-testid={`category-card-${category.key}`}
    >
      {coverThumb ? (
        <img src={coverThumb} alt="" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-slate-700 to-slate-900" />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
      <div className="absolute inset-0 flex flex-col justify-end p-5 md:p-6">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-9 h-9 rounded-lg bg-white/15 backdrop-blur-sm flex items-center justify-center">
            <Icon className="w-5 h-5 text-white" />
          </div>
          <span className="text-white/70 text-xs font-semibold bg-white/10 backdrop-blur-sm px-2.5 py-1 rounded-full">
            {videoCount} {videoCount !== 1 ? t('media.videos', language) : t('media.video', language)}
          </span>
        </div>
        <h3 className="text-white font-bold text-lg md:text-xl leading-tight line-clamp-2">{category.label[language] || category.label.en}</h3>
      </div>
      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
        <div className="w-16 h-16 bg-amber-400/90 rounded-full flex items-center justify-center shadow-[0_0_30px_rgba(252,211,77,0.4)]">
          <Play className="w-7 h-7 text-slate-950 fill-slate-950 ml-0.5" />
        </div>
      </div>
    </motion.div>
  );
};

// Main Media Page
const MediaPage = () => {
  const [videos, setVideos] = useState([]);
  const [coverImages, setCoverImages] = useState({});
  const [activeView, setActiveView] = useState('categories');
  const [activeVideoId, setActiveVideoId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [galleryPhotos, setGalleryPhotos] = useState([]);
  const [lightboxPhoto, setLightboxPhoto] = useState(null);
  const { language } = useLanguage();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [data, covers] = await Promise.all([
          getVideos(),
          getCategoryCovers().catch(() => [])
        ]);
        // Fetch gallery photos
        getImages('img_new_pics_website').then(photos => {
          setGalleryPhotos(photos.filter(p => p.published !== false));
        }).catch(() => {});
        const mediaKeys = new Set(CATEGORIES.map(c => c.key));
        const mapped = data.map(v => {
          const mappedCat = LEGACY_MAP[v.category] || v.category;
          return { ...v, category: mappedCat };
        }).filter(v => mediaKeys.has(v.category) && v.source !== 'placeholder');
        setVideos(mapped);
        
        const coverMap = {};
        covers.forEach(c => { if (c.cover_image) coverMap[c.category_key] = c.cover_image; });
        setCoverImages(coverMap);
      } catch (err) {
        console.error('Failed to load videos:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const videosByCategory = {};
  CATEGORIES.forEach(c => { videosByCategory[c.key] = []; });
  videos.forEach(v => {
    if (videosByCategory[v.category]) videosByCategory[v.category].push(v);
  });
  // Sort by display_order so admin reorder syncs to public page
  Object.keys(videosByCategory).forEach(key => {
    videosByCategory[key].sort((a, b) => (a.display_order ?? 999) - (b.display_order ?? 999));
  });

  const getCoverThumb = (catKey) => {
    if (coverImages[catKey]) return coverImages[catKey];
    const catVids = videosByCategory[catKey] || [];
    for (const v of catVids) {
      const thumb = getThumbUrl(v);
      if (thumb) return thumb;
    }
    return null;
  };

  const currentCatVideos = activeView !== 'categories' ? (videosByCategory[activeView] || []) : [];
  const currentCat = CATEGORIES.find(c => c.key === activeView);
  const [detectedVertical, setDetectedVertical] = useState(null);

  const activeVideo = activeVideoId ? videos.find(v => v.id === activeVideoId) : null;

  // Use detected orientation from video stream, fall back to DB dimensions, default to vertical
  const isVertical = detectedVertical !== null
    ? detectedVertical
    : (activeVideo?.video_width && activeVideo?.video_height && activeVideo.video_width > 0 && activeVideo.video_height > 0)
      ? activeVideo.video_height > activeVideo.video_width
      : true;

  // Reset detected orientation when video changes
  useEffect(() => {
    setDetectedVertical(null);
  }, [activeVideoId]);

  const openCategory = useCallback((catKey) => {
    setActiveVideoId(null);
    setActiveView(catKey);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const goBack = useCallback(() => {
    setActiveVideoId(null);
    setActiveView('categories');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const closePlayer = useCallback(() => {
    setActiveVideoId(null);
  }, []);

  // Build Cloudflare iframe URL
  const getIframeSrc = (video) => {
    if (video.source === 'cloudflare' && video.cloudflare_uid) {
      return `https://${CF_CUSTOMER}/${video.cloudflare_uid}/iframe?autoplay=true&preload=auto&controls=true`;
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-[#020617] pt-20" data-testid="media-page">
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-amber-400/3 rounded-full blur-[150px]" />
        <div className="absolute inset-0 opacity-[0.015]" style={{
          backgroundImage: 'radial-gradient(circle at 25% 25%, white 1px, transparent 1px)',
          backgroundSize: '60px 60px'
        }} />
      </div>

      {/* Under Construction Overlay — set to true to enable */}
      {false && (
      <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/80 backdrop-blur-md" data-testid="under-construction-overlay">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="relative max-w-lg w-full mx-4"
        >
          {/* Glow effect */}
          <div className="absolute -inset-1 bg-gradient-to-r from-amber-400/20 via-amber-200/10 to-amber-400/20 rounded-3xl blur-xl" />
          
          <div className="relative bg-slate-900/90 border border-white/10 rounded-3xl p-8 md:p-12 text-center backdrop-blur-sm">
            {/* Animated construction icon */}
            <div className="relative mx-auto w-20 h-20 mb-6">
              <div className="absolute inset-0 bg-amber-400/20 rounded-2xl rotate-6" />
              <div className="absolute inset-0 bg-amber-400/10 rounded-2xl -rotate-6" />
              <div className="relative w-full h-full bg-gradient-to-br from-amber-400 to-amber-500 rounded-2xl flex items-center justify-center shadow-lg shadow-amber-400/25">
                <Film className="w-9 h-9 text-slate-950" />
              </div>
            </div>

            {/* Pulsing bar */}
            <div className="w-16 h-1 bg-gradient-to-r from-transparent via-amber-400 to-transparent mx-auto mb-6 rounded-full animate-pulse" />

            <h2 className="text-2xl md:text-3xl font-black text-white tracking-tight mb-3">
              {language === 'en' ? 'Under Construction' : 'En Construcción'}
            </h2>
            
            <p className="text-slate-300 text-base leading-relaxed mb-4">
              {language === 'en'
                ? 'We\'re uploading exciting new videos to our cloud storage. This page will be available to view very soon!'
                : '¡Estamos subiendo emocionantes videos nuevos a nuestro almacenamiento en la nube. Esta página estará disponible para ver muy pronto!'}
            </p>

            <p className="text-slate-500 text-sm italic">
              {language === 'en'
                ? 'We apologize for the inconvenience.'
                : 'Disculpe las molestias.'}
            </p>

            {/* Animated dots loader */}
            <div className="flex items-center justify-center gap-1.5 mt-6">
              {[0, 1, 2].map(i => (
                <div
                  key={i}
                  className="w-2 h-2 rounded-full bg-amber-400"
                  style={{ animation: 'pulse 1.4s ease-in-out infinite', animationDelay: `${i * 0.2}s` }}
                />
              ))}
            </div>
          </div>
        </motion.div>
      </div>
      )}

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 pt-10 pb-20">
        <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="text-center mb-10">
          <span className="uppercase tracking-[0.3em] text-[11px] font-bold text-amber-400 mb-2 block">{t('media.badge', language)}</span>
          <h1 className="text-5xl md:text-7xl font-black text-white tracking-tight">{t('media.title', language)}</h1>
          <p className="text-slate-400 text-base md:text-lg mt-3 max-w-xl mx-auto">{t('media.subtitle', language)}</p>
        </motion.div>

        <AnimatePresence mode="wait">
          {activeView === 'categories' ? (
            <motion.div key="categories" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.3 }}>
              {loading ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <div key={i} className="aspect-[3/2] rounded-2xl bg-slate-800/40 animate-pulse" />
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {CATEGORIES.map((cat, index) => (
                    <CategoryCard
                      key={cat.key}
                      category={cat}
                      videoCount={(videosByCategory[cat.key] || []).length}
                      coverThumb={getCoverThumb(cat.key)}
                      onClick={() => openCategory(cat.key)}
                      index={index}
                      language={language}
                    />
                  ))}
                </div>
              )}
            </motion.div>
          ) : (
            <motion.div key={activeView} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 30 }} transition={{ duration: 0.3 }}>
              <div className="mb-8">
                <button onClick={goBack} className="flex items-center gap-2 text-slate-400 hover:text-amber-400 transition-colors mb-4 group" data-testid="back-to-categories">
                  <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
                  <span className="text-sm font-medium">{t('media.backToCollections', language)}</span>
                </button>
                <div className="flex items-center gap-3">
                  {currentCat && (
                    <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${currentCat.color} flex items-center justify-center`}>
                      <currentCat.icon className="w-6 h-6 text-white" />
                    </div>
                  )}
                  <div>
                    <h2 className="text-2xl md:text-3xl font-bold text-white">{currentCat?.label[language] || currentCat?.label.en}</h2>
                    <p className="text-slate-500 text-sm mt-0.5">
                      {currentCatVideos.length} {currentCatVideos.length !== 1 ? t('media.videos', language) : t('media.video', language)}
                    </p>
                  </div>
                </div>
              </div>

              {currentCatVideos.length === 0 ? (
                <div className="text-center py-20">
                  <Film className="w-14 h-14 text-slate-700 mx-auto mb-3" />
                  <p className="text-slate-500 text-lg">{t('media.noVideos', language)}</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3 sm:gap-4">
                  {currentCatVideos.map((video) => (
                    <VideoCard
                      key={video.id}
                      video={video}
                      isActive={activeVideoId === video.id}
                      onClick={() => setActiveVideoId(video.id)}
                      language={language}
                    />
                  ))}
                </div>
              )}

              {currentCatVideos.length > 4 && (
                <div className="mt-12 text-center">
                  <button onClick={goBack} className="inline-flex items-center gap-2 px-6 py-3 bg-white/5 hover:bg-white/10 border border-white/10 rounded-full text-slate-300 hover:text-white transition-all text-sm font-medium" data-testid="back-bottom">
                    <ArrowLeft className="w-4 h-4" />
                    {t('media.backToCollections', language)}
                  </button>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* ═══════════════ PHOTO GALLERY ═══════════════ */}
      {galleryPhotos.length > 0 && (
        <section className="relative mt-8" data-testid="photo-gallery-section">
          {/* Divider accent */}
          <div className="flex items-center gap-4 px-4 md:px-8 max-w-7xl mx-auto mb-12">
            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-amber-400/40 to-transparent" />
            <div className="flex items-center gap-3">
              <Camera className="w-5 h-5 text-amber-400" />
              <h2 className="text-xl md:text-2xl font-black text-white tracking-wide uppercase">
                {language === 'en' ? 'Gallery' : 'Galería'}
              </h2>
              <Camera className="w-5 h-5 text-amber-400" />
            </div>
            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-amber-400/40 to-transparent" />
          </div>

          {/* Masonry Photo Grid */}
          <div className="px-2 md:px-4 max-w-7xl mx-auto columns-2 md:columns-3 lg:columns-4 gap-2 md:gap-3" data-testid="photo-masonry-grid">
            {galleryPhotos.map((photo, i) => (
              <div
                key={photo.id}
                className="break-inside-avoid mb-2 md:mb-3 group cursor-pointer relative overflow-hidden rounded-lg"
                onClick={() => setLightboxPhoto(photo)}
                data-testid={`gallery-photo-${photo.id}`}
              >
                <img
                  src={photo.url}
                  alt={photo.alt_text || photo.title || ''}
                  loading="lazy"
                  className="w-full block transition-transform duration-700 ease-out group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Lightbox */}
      <AnimatePresence>
        {lightboxPhoto && (
          <motion.div
            key="lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-black/95 flex items-center justify-center p-4 cursor-pointer"
            onClick={() => setLightboxPhoto(null)}
            data-testid="photo-lightbox"
          >
            <button
              className="absolute top-4 right-4 z-10 w-10 h-10 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center transition-colors"
              onClick={(e) => { e.stopPropagation(); setLightboxPhoto(null); }}
              data-testid="lightbox-close"
            >
              <X className="w-5 h-5 text-white" />
            </button>
            <motion.img
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.3 }}
              src={lightboxPhoto.url}
              alt={lightboxPhoto.alt_text || lightboxPhoto.title || ''}
              className="max-w-[92vw] max-h-[90vh] object-contain rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal Video Player — Cloudflare iframe (proven to work) */}
      <AnimatePresence>
        {activeVideo && (
          <motion.div
            key="modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-6"
            data-testid="modal-player-overlay"
            onClick={closePlayer}
          >
            <div className="absolute inset-0 bg-black/90" />

            <motion.div
              initial={{ scale: 0.92 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.92 }}
              onClick={(e) => e.stopPropagation()}
              className={`relative z-10 rounded-2xl overflow-hidden bg-black shadow-[0_0_60px_rgba(252,211,77,0.15)] border border-amber-400/20 ${
                isVertical
                  ? 'w-[85vw] sm:w-[55vw] md:w-[40vw] lg:w-[30vw] max-h-[88vh]'
                  : 'w-[95vw] sm:w-[90vw] md:w-[85vw] lg:w-[75vw] max-h-[88vh]'
              }`}
              data-testid={`video-playing-${activeVideo.id}`}
            >
              <button
                onClick={closePlayer}
                className="absolute top-3 right-3 z-20 p-2.5 bg-black/70 hover:bg-red-600/80 rounded-full text-white/80 hover:text-white transition-all"
                data-testid="close-player"
              >
                <X className="w-5 h-5" />
              </button>

              <div className="absolute top-3 left-3 z-20 flex items-center gap-1.5 bg-amber-400 text-slate-950 text-xs font-bold px-3 py-1.5 rounded-full">
                <Volume2 className="w-3.5 h-3.5" />
                {t('media.nowPlaying', language)}
              </div>

              <div className={isVertical ? 'aspect-[9/16]' : 'aspect-video'}>
                {activeVideo.cloudflare_uid ? (
                  <HlsPlayer videoId={activeVideo.cloudflare_uid} autoplay={true} onOrientationDetected={setDetectedVertical} />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <p className="text-white/40">{t('media.videoUnavailable', language)}</p>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MediaPage;
